import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Toast a bottom sheet – jeden globální stav, markup podle prototypu. */

interface UiContextValue {
  toast: (message: string) => void;
  openSheet: (content: ReactNode) => void;
  closeSheet: () => void;
}

const UiContext = createContext<UiContextValue | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [sheet, setSheet] = useState<ReactNode>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const toast = useCallback((message: string) => {
    setToastMsg(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToastMsg(null), 2400);
  }, []);
  const openSheet = useCallback((content: ReactNode) => setSheet(content), []);
  const closeSheet = useCallback(() => setSheet(null), []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => ({ toast, openSheet, closeSheet }), [toast, openSheet, closeSheet]);

  return (
    <UiContext.Provider value={value}>
      {children}
      <div id="modal-root">
        {sheet && (
          <div
            className="sheet-bg"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeSheet();
            }}
          >
            <div className="sheet" role="dialog" aria-modal="true">
              {sheet}
            </div>
          </div>
        )}
      </div>
      <div id="toast" className={toastMsg ? 'show' : ''} role="status" aria-live="polite">
        {toastMsg}
      </div>
    </UiContext.Provider>
  );
}

export function useUi(): UiContextValue {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi mimo UiProvider');
  return ctx;
}
